import React, { useRef } from 'react';
import './FileInput.css';

/**
 * FileInput Component
 * Standardized file upload input
 * 
 * @param {string} label - Input label
 * @param {string} accept - Accepted file types (e.g., "image/*", ".pdf")
 * @param {boolean} multiple - Allow multiple files
 * @param {function} onChange - Change handler: (files: FileList) => void
 * @param {string} error - Error message
 * @param {boolean} disabled - Disabled state
 * @param {boolean} required - Required field indicator
 * @param {number} maxSize - Maximum file size in bytes
 * @param {string} helperText - Helper text below input
 * @param {string} className - Additional CSS classes
 */
const FileInput = ({
  label,
  accept,
  multiple = false,
  onChange,
  error,
  disabled = false,
  required = false,
  maxSize,
  helperText,
  className = ''
}) => {
  const fileInputRef = useRef(null);
  const [selectedFiles, setSelectedFiles] = React.useState([]);

  const handleChange = (e) => {
    const files = Array.from(e.target.files || []);
    
    // Validate file sizes
    if (maxSize) {
      const invalidFiles = files.filter(file => file.size > maxSize);
      if (invalidFiles.length > 0) {
        // Could trigger error state here
        return;
      }
    }
    
    setSelectedFiles(files);
    if (onChange) {
      onChange(e.target.files);
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const inputId = `file-input-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={`file-input-container ${error ? 'error' : ''} ${className}`.trim()}>
      {label && (
        <label className="file-input-label">
          {label}
          {required && <span className="file-input-required">*</span>}
        </label>
      )}
      <div className="file-input-wrapper">
        <input
          type="file"
          id={inputId}
          ref={fileInputRef}
          accept={accept}
          multiple={multiple}
          onChange={handleChange}
          disabled={disabled}
          className="file-input"
          aria-required={required}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined}
        />
        <button
          type="button"
          onClick={handleClick}
          disabled={disabled}
          className="file-input-button"
        >
          <span className="file-input-icon">📁</span>
          <span className="file-input-button-text">
            {selectedFiles.length > 0 
              ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} selected`
              : 'Choose file' + (multiple ? 's' : '')
            }
          </span>
        </button>
      </div>
      {selectedFiles.length > 0 && (
        <div className="file-input-list">
          {selectedFiles.map((file, index) => (
            <div key={index} className="file-input-item">
              <span className="file-input-name">{file.name}</span>
              <span className="file-input-size">{formatFileSize(file.size)}</span>
            </div>
          ))}
        </div>
      )}
      {(error || helperText) && (
        <div className="file-input-footer">
          {error && (
            <span id={`${inputId}-error`} className="file-input-error" role="alert">
              {error}
            </span>
          )}
          {!error && helperText && (
            <span id={`${inputId}-helper`} className="file-input-helper">
              {helperText}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default FileInput;










